import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject
} from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import dayjs from 'dayjs';

import { APP_STORE } from '@core/stores/app.store';
import { TemplatingPipe } from '@shared/pipes/templating.pipe';
import { AttachmentsService } from '@file-upload/services/attachments.service';
import { DownloadReportNotificationData } from '@notifications/models/download-report-notification-data';
import { Notification } from '@notifications/models/notification';
import { NotificationStatus } from '@notifications/models/notification-status';
import { NotificationEntitiesService } from '@notifications/services/notification-entities.service';
import { NOTIFICATIONS_STORE } from '@notifications/stores/notifications.store';

@Component({
  selector: 'app-notifications-sidenav',
  templateUrl: './notifications-sidenav.component.html',
  styleUrls: ['./notifications-sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MatButton, MatIcon, MatIconButton]
})
export class NotificationsSidenavComponent {
  readonly #notificationsStore = inject(NOTIFICATIONS_STORE);
  readonly #notificationEntitiesService = inject(NotificationEntitiesService);
  readonly #attachmentsService = inject(AttachmentsService);
  readonly #templatingPipe = inject(TemplatingPipe);
  readonly #appStore = inject(APP_STORE);

  readonly notifications = computed((): Notification[] => {
    const entities = this.#notificationsStore.entities();
    const selectedClientId = this.#appStore.selectedClientId();
    const selectedProjectId = this.#appStore.selectedProjectId();

    return entities.filter(
      n => n.clientId === selectedClientId && n.projectId === selectedProjectId
    );
  });

  readonly NotificationsStatus = NotificationStatus;

  async downloadReport(data: DownloadReportNotificationData): Promise<void> {
    await this.#attachmentsService.download(data.file);
  }

  removeNotification(notificationId: number): void {
    this.#notificationsStore.removeNotification(notificationId);
  }

  onPanelClose(): void {
    this.#notificationsStore.toggleNotificationsSidenav();
  }

  getTitle(notification: Notification): string {
    return this.#notificationEntitiesService.entities[notification.type]
      .messages[notification.status].title;
  }

  getMessage(notification: Notification): string {
    return this.#templatingPipe.transform(
      this.#notificationEntitiesService.entities[notification.type].messages[
        notification.status
      ].message,
      notification.templateVariables
    );
  }

  getActionLabel(notification: Notification): string {
    return this.#notificationEntitiesService.entities[notification.type].label;
  }

  getDate(date: Date): string {
    return dayjs(date).fromNow();
  }
}
